import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  ChevronRight,
  ChevronDown,
  Zap,
  Target,
  RotateCcw,
} from "lucide-react";

// Grade helper functions
export const getGradeLabel = (grade) => {
  if (!grade) return { label: "Not Evaluated", color: "text-gray-400 bg-gray-50" };
  if (grade >= 80) return { label: "Elite", color: "text-purple-700 bg-purple-100" };
  if (grade >= 70) return { label: "Plus-Plus", color: "text-green-700 bg-green-100" };
  if (grade >= 60) return { label: "Plus", color: "text-green-600 bg-green-50" };
  if (grade >= 55) return { label: "Above Avg", color: "text-blue-600 bg-blue-50" };
  if (grade >= 50) return { label: "Average", color: "text-gray-600 bg-gray-100" };
  if (grade >= 45) return { label: "Below Avg", color: "text-yellow-600 bg-yellow-50" };
  if (grade >= 40) return { label: "Fringe", color: "text-orange-600 bg-orange-50" };
  return { label: "Poor", color: "text-red-600 bg-red-50" };
};

// Sub-Factor Input Component
export const SubFactorInput = ({ 
  factorKey, 
  factorDef, 
  value, 
  onChange, 
  showNotes = false 
}) => {
  const [expanded, setExpanded] = useState(false);
  const grade = value?.grade;
  const gradeInfo = getGradeLabel(grade);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 bg-white hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-800">{factorDef.name}</span>
            <span className="text-xs text-gray-500">{Math.round(factorDef.weight * 100)}% weight</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {grade ? (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gray-900">{grade}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${gradeInfo.color}`}>
                {gradeInfo.label}
              </span>
            </div>
          ) : (
            <span className="text-sm text-gray-400 italic">Not evaluated</span>
          )}
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      
      {expanded && (
        <div className="p-3 bg-gray-50 border-t border-gray-200 space-y-3">
          <p className="text-xs text-gray-600">{factorDef.description}</p>
          <div className="text-xs text-gray-500">
            <strong>Inputs:</strong> {factorDef.inputs?.join(", ")}
          </div>
          
          {/* Grade Slider */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label className="text-xs text-gray-600">Grade (20-80)</Label>
              <span className="text-sm font-bold text-gray-900">{grade || "--"}</span>
            </div>
            <input
              type="range"
              min="20"
              max="80"
              step="5"
              value={grade || 50}
              onChange={(e) => onChange(factorKey, { ...value, grade: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: grade ? `linear-gradient(to right, #2563eb 0%, #2563eb ${((grade - 20) / 60) * 100}%, #e5e7eb ${((grade - 20) / 60) * 100}%, #e5e7eb 100%)` : '#e5e7eb'
              }}
            />
            <div className="flex justify-between text-xs text-gray-400 px-1">
              <span>20</span><span>40</span><span>50</span><span>60</span><span>80</span>
            </div>
          </div>
          
          {/* Clear button */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); onChange(factorKey, null); }}
              className="text-xs"
            >
              Clear (Not Evaluated)
            </Button>
          </div>
          
          {showNotes && (
            <div className="space-y-1">
              <Label className="text-xs text-gray-600">Notes</Label>
              <Input
                placeholder="Optional notes for this factor..."
                value={value?.notes || ""}
                onChange={(e) => onChange(factorKey, { ...value, notes: e.target.value })}
                className="text-sm"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Tool Section Component with Sub-factors
export const ToolSection = ({ 
  title, 
  description, 
  factors, 
  values, 
  onChange, 
  overrideGrade, 
  onOverrideChange,
  calculatedGrade,
  confidence,
  expanded,
  onToggle
}) => {
  const gradeInfo = getGradeLabel(overrideGrade || calculatedGrade);
  const evaluatedCount = Object.keys(factors).filter(k => values[k]?.grade).length;
  const totalCount = Object.keys(factors).length;
  
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div 
        className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white cursor-pointer hover:from-gray-100"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Target className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Evaluation Status */}
          <div className="text-right">
            <div className="flex items-center gap-2">
              {calculatedGrade ? (
                <>
                  <span className="text-2xl font-bold text-gray-900">{overrideGrade || calculatedGrade}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${gradeInfo.color}`}>
                    {gradeInfo.label}
                  </span>
                </>
              ) : (
                <span className="text-sm text-gray-400">No data</span>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
              <span>{evaluatedCount}/{totalCount} factors</span>
              {confidence !== undefined && <span className="text-blue-600">({confidence}% confidence)</span>}
            </div>
          </div>
          {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 border-t border-gray-200 space-y-4">
          {/* Sub-factors */}
          <div className="space-y-2">
            {Object.entries(factors).map(([key, def]) => (
              <SubFactorInput
                key={key}
                factorKey={key}
                factorDef={def}
                value={values[key]}
                onChange={(k, v) => onChange({ ...values, [k]: v })}
                showNotes={true}
              />
            ))}
          </div>
          
          {/* Override Grade */}
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-yellow-800">Scout Override</Label>
                <p className="text-xs text-yellow-600">Override the calculated grade with your expert assessment</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="20"
                  max="80"
                  step="5"
                  placeholder="--"
                  value={overrideGrade || ""}
                  onChange={(e) => onOverrideChange(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-20 text-center font-bold"
                />
                {overrideGrade && (
                  <Button variant="ghost" size="sm" onClick={() => onOverrideChange(null)}>
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Hit Tool Composite Section (combines Hit for Avg + Hit for Power)
export const HitToolSection = ({
  hitForAvgFactors,
  hitForPowerFactors,
  hitForAvgValues,
  hitForPowerValues,
  onHitForAvgChange,
  onHitForPowerChange,
  hitForAvgOverride,
  hitForPowerOverride,
  hitOverallOverride,
  onHitForAvgOverrideChange,
  onHitForPowerOverrideChange,
  onHitOverallOverrideChange,
  position,
  weights
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showAvg, setShowAvg] = useState(false);
  const [showPower, setShowPower] = useState(false);
  
  // Calculate grades
  const calcGrade = (values, factors, override) => {
    if (override) return override;
    const evaluated = Object.entries(factors).filter(([k]) => values[k]?.grade);
    if (evaluated.length === 0) return null;
    const totalWeight = evaluated.reduce((sum, [k, f]) => sum + f.weight, 0);
    const weightedSum = evaluated.reduce((sum, [k, f]) => sum + (values[k].grade * f.weight), 0);
    return Math.round(weightedSum / totalWeight);
  };
  
  const avgGrade = calcGrade(hitForAvgValues, hitForAvgFactors, hitForAvgOverride);
  const powerGrade = calcGrade(hitForPowerValues, hitForPowerFactors, hitForPowerOverride);
  
  let compositeGrade = null;
  if (hitOverallOverride) {
    compositeGrade = hitOverallOverride;
  } else if (avgGrade && powerGrade) {
    compositeGrade = Math.round(avgGrade * weights.average + powerGrade * weights.power);
  } else if (avgGrade) {
    compositeGrade = avgGrade;
  } else if (powerGrade) {
    compositeGrade = powerGrade;
  }
  
  const gradeInfo = getGradeLabel(compositeGrade);
  
  // Determine hitter profile
  let profile = "Incomplete Evaluation";
  if (avgGrade && powerGrade) {
    if (avgGrade > powerGrade + 10) profile = "Contact-First Hitter";
    else if (powerGrade > avgGrade + 10) profile = "Power-First Hitter";
    else if (avgGrade >= 55 && powerGrade >= 55) profile = "Complete Hitter";
    else profile = "Developing Hitter";
  }
  
  return (
    <div className="border-2 border-blue-200 rounded-xl overflow-hidden bg-white shadow-md">
      <div 
        className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-white cursor-pointer hover:from-blue-100"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Hit Tool</h3>
            <p className="text-xs text-gray-500">
              Hit for Average ({Math.round(weights.average * 100)}%) + Hit for Power ({Math.round(weights.power * 100)}%)
            </p>
            <p className="text-xs text-blue-600 font-medium">{position} weighting</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            {compositeGrade ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-gray-900">{compositeGrade}</span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${gradeInfo.color}`}>
                    {gradeInfo.label}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{profile}</p>
              </>
            ) : (
              <span className="text-sm text-gray-400">Not evaluated</span>
            )}
          </div>
          {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
        </div>
      </div>
      
      {expanded && (
        <div className="border-t border-blue-200">
          {/* Hit for Average Section */}
          <div className="border-b border-gray-200">
            <div 
              className="flex items-center justify-between p-3 bg-green-50 cursor-pointer hover:bg-green-100"
              onClick={() => setShowAvg(!showAvg)}
            >
              <div>
                <h4 className="font-semibold text-green-800">Hit for Average</h4>
                <p className="text-xs text-green-600">Barrel Consistency + Decision Quality</p>
              </div>
              <div className="flex items-center gap-3">
                {avgGrade ? (
                  <span className="text-xl font-bold text-green-800">{avgGrade}</span>
                ) : (
                  <span className="text-sm text-green-600">--</span>
                )}
                {showAvg ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            </div>
            {showAvg && (
              <div className="p-4 space-y-2 bg-white">
                {Object.entries(hitForAvgFactors).map(([key, def]) => (
                  <SubFactorInput
                    key={key}
                    factorKey={key}
                    factorDef={def}
                    value={hitForAvgValues[key]}
                    onChange={(k, v) => onHitForAvgChange({ ...hitForAvgValues, [k]: v })}
                    showNotes={true}
                  />
                ))}
                <div className="mt-3 p-2 bg-green-50 rounded flex items-center justify-between">
                  <span className="text-sm text-green-700">Override Grade:</span>
                  <Input
                    type="number"
                    min="20"
                    max="80"
                    step="5"
                    placeholder="--"
                    value={hitForAvgOverride || ""}
                    onChange={(e) => onHitForAvgOverrideChange(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-20 text-center"
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Hit for Power Section */}
          <div className="border-b border-gray-200">
            <div 
              className="flex items-center justify-between p-3 bg-orange-50 cursor-pointer hover:bg-orange-100"
              onClick={() => setShowPower(!showPower)}
            >
              <div>
                <h4 className="font-semibold text-orange-800">Hit for Power</h4>
                <p className="text-xs text-orange-600">Impact Frequency + Damage Ceiling</p>
              </div>
              <div className="flex items-center gap-3">
                {powerGrade ? (
                  <span className="text-xl font-bold text-orange-800">{powerGrade}</span>
                ) : (
                  <span className="text-sm text-orange-600">--</span>
                )}
                {showPower ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            </div>
            {showPower && (
              <div className="p-4 space-y-2 bg-white">
                {Object.entries(hitForPowerFactors).map(([key, def]) => (
                  <SubFactorInput
                    key={key}
                    factorKey={key}
                    factorDef={def}
                    value={hitForPowerValues[key]}
                    onChange={(k, v) => onHitForPowerChange({ ...hitForPowerValues, [k]: v })}
                    showNotes={true}
                  />
                ))}
                <div className="mt-3 p-2 bg-orange-50 rounded flex items-center justify-between">
                  <span className="text-sm text-orange-700">Override Grade:</span>
                  <Input
                    type="number"
                    min="20"
                    max="80"
                    step="5"
                    placeholder="--"
                    value={hitForPowerOverride || ""}
                    onChange={(e) => onHitForPowerOverrideChange(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-20 text-center"
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Overall Hit Tool Override */}
          <div className="p-4 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold text-yellow-800">Overall Hit Tool Override</Label>
                <p className="text-xs text-yellow-600">Override the composite calculation</p>
              </div>
              <Input
                type="number"
                min="20"
                max="80"
                step="5"
                placeholder="--"
                value={hitOverallOverride || ""}
                onChange={(e) => onHitOverallOverrideChange(e.target.value ? parseInt(e.target.value) : null)}
                className="w-20 text-center font-bold"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
